import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import SkillBadge from './skillBadge';

const MultiSelectDropdown = ({
  label,
  options = [],
  selected,
  onChange,
  name,
  isMultiSelect = true,
  isLoading = false,
  placeholder = '',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const selectedArray = isMultiSelect
    ? Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : []
    : selected || '';

  const getLabel = useCallback((item) => {
    if (!item) return '';
    return typeof item === 'string' ? item : item.label || item.name || item.value || '';
  }, []);

  const isSelected = useCallback(
    (item) => selectedArray.includes(getLabel(item)),
    [selectedArray, getLabel]
  );

  const handleClickOutside = useCallback((e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setIsOpen(false);
      setSearchTerm('');
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const label = getLabel(opt).toLowerCase();
      return isMultiSelect
        ? label.includes(term) && !selectedArray.includes(label)
        : label.includes(term) && label !== selectedArray;
    });
  }, [options, searchTerm, selectedArray, isMultiSelect, getLabel]);

  const handleSelect = useCallback(
    (option) => {
      const label = getLabel(option);
      if (!label) return;

      if (isMultiSelect) {
        const updated = selectedArray.includes(label)
          ? selectedArray.filter((val) => val !== label)
          : [...selectedArray, label];
        onChange({ target: { name, value: updated } });
      } else {
        const updated = selectedArray === label ? '' : label;
        onChange({ target: { name, value: updated } });
        setIsOpen(false);
      }
      setSearchTerm('');
    },
    [getLabel, isMultiSelect, onChange, name, selectedArray]
  );

  const handleRemove = useCallback(
    (item) => {
      const label = getLabel(item);
      if (!label) return;

      if (isMultiSelect) {
        const updated = selectedArray.filter((val) => val !== label);
        onChange({ target: { name, value: updated } });
      } else {
        onChange({ target: { name, value: '' } });
      }
    },
    [getLabel, isMultiSelect, onChange, name, selectedArray]
  );

  const renderChips = useCallback(() => {
    if (!isMultiSelect) {
      const label = getLabel(selectedArray);
      return label ? (
        <SkillBadge>
          {label}
          <button
            type="button"
            onClick={() => handleRemove(selectedArray)}
            className="ml-1 p-0 border-none bg-transparent cursor-pointer"
          >
            <FiX size={12} />
          </button>
        </SkillBadge>
      ) : null;
    }

    return selectedArray
      .filter(Boolean)
      .map((item, idx) => {
        const label = getLabel(item);
        return (
          <SkillBadge key={`${label}_${idx}`}>
            {label}
            <button
              type="button"
              onClick={() => handleRemove(item)}
              className="ml-1 p-0 border-none bg-transparent cursor-pointer"
            >
              <FiX size={12} />
            </button>
          </SkillBadge>
        );
      });
  }, [getLabel, handleRemove, selectedArray, isMultiSelect]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && <label className="text-xs font-semibold text-gray-700">{label}</label>}

      <div className="flex flex-wrap gap-2 my-1">{renderChips()}</div>

      <div className="relative w-full">
        <input
          type="text"
          placeholder={placeholder || (isMultiSelect ? 'Search and select to add...' : 'Search and select...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          disabled={isLoading || disabled}
          className={`w-full p-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
        />

        {isOpen && (
          <div  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg overflow-y-auto"
  style={{ bottom: '100%', maxHeight: 'calc(6 * 2.5rem)' }}>
            {isLoading ? (
              <div className="p-2 text-xs text-gray-500">Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => {
                const label = getLabel(opt);
                return (
                  <button
                    key={`${label}_${idx}`}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                  >
                    {label}
                  </button>
                );
              })
            ) : (
              <div className="p-2 text-xs text-gray-500">No options found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
